import {createStyles, Group, Stack, Text, Title} from '@mantine/core';
import React, {useMemo} from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import RoutineCard from './RoutineCard';
import {RoutineStatus, TriggerType} from './RoutineTypes';
import useRoutines from './useRoutines';

interface RoutinesPageProps {
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles(() => ({
  container: {
    maxWidth: 540,
    width: '100%',
    margin: '0 auto',
  },
}));

export default function RoutinesPage({timeBasedEnabled, eventBasedEnabled}: RoutinesPageProps): JSX.Element {
  const {classes} = useStyles();
  const {routines, isLoading, isError} = useRoutines();

  const visibleRoutines = useMemo(
    () =>
      routines.filter(
        (s) =>
          (timeBasedEnabled && s.triggerType === TriggerType.TIME_BASED) ||
          (eventBasedEnabled && s.triggerType === TriggerType.EVENT_BASED)
      ),
    [routines, timeBasedEnabled, eventBasedEnabled]
  );

  const activeRoutines = useMemo(
    () =>
      visibleRoutines.filter(
        (s) => s.status === RoutineStatus.ACTIVE || s.status === RoutineStatus.PENDING_REGISTRATION
      ),
    [visibleRoutines]
  );
  const pausedRoutines = useMemo(
    () => visibleRoutines.filter((s) => s.status === RoutineStatus.PAUSED),
    [visibleRoutines]
  );

  if (isLoading) {
    return (
      <Stack className={classes.container} spacing="md">
        <Text size="xs" color="dimmed">
          Loading routines...
        </Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack className={classes.container} spacing="md">
        <Text size="xs" color="dimmed">
          Failed to load routines. Please try again.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack className={classes.container} spacing="md">
      <Stack spacing="xs">
        <Title order={2} size="h4">
          Routines
        </Title>
        <Text size="sm" color="dimmed">
          Set up routines to run automatically at a set time or triggered by an event.
        </Text>
      </Stack>

      <Group position="apart">
        <Text size="xs" weight={600}>
          Active Routines
        </Text>
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          icon="plus"
          size={ButtonSize.SMALL}
          text="New Routine"
          onClick={() => {
            // TODO(nweeden): Navigate to routine creation
          }}
        />
      </Group>

      <Stack spacing="md">
        {activeRoutines.map((routine) => (
          <RoutineCard key={routine.id} routine={routine} />
        ))}
        {activeRoutines.length === 0 && (
          <Text size="xs" color="dimmed">
            No active routines
          </Text>
        )}
      </Stack>

      {pausedRoutines.length > 0 && (
        <>
          <Text size="xs" weight={600}>
            Paused Routines
          </Text>
          <Stack spacing="md">
            {pausedRoutines.map((routine) => (
              <RoutineCard key={routine.id} routine={routine} />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
